// API timestamps are UTC unless an explicit offset is supplied.
// Date-only calendar values are not instants and never shift across timezones.
const ISO=/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,6}))?)?(Z|[+-]\d{2}:?\d{2})?$/i;
const DAY=/^(\d{4})-(\d{2})-(\d{2})$/;
function calendarValid(year,month,day) {
 const d=new Date(0);d.setUTCFullYear(year,month-1,day);d.setUTCHours(0,0,0,0);
 return d.getUTCFullYear()===year&&d.getUTCMonth()===month-1&&d.getUTCDate()===day;
}
export function parseTimestamp(value) {
 if(value instanceof Date)return Number.isNaN(value.getTime())?null:new Date(value.getTime());
 if(typeof value==='number')return Number.isFinite(value)&&!Number.isNaN(new Date(value).getTime())?new Date(value):null;
 if(typeof value!=='string'||!value.trim())return null;
 const input=value.trim(),match=input.match(ISO);
 if(match){
  const [,y,m,d,h,min,s='00',fraction='',offset='Z']=match;
  if(!calendarValid(+y,+m,+d)||+h>23||+min>59||+s>59)return null;
  const zone=offset.toUpperCase()==='Z'?'Z':offset.replace(/^([+-]\d{2})(\d{2})$/,'$1:$2');
  const result=new Date(y+'-'+m+'-'+d+'T'+h+':'+min+':'+s+(fraction?'.'+fraction.slice(0,3).padEnd(3,'0'):'')+zone);
  return Number.isNaN(result.getTime())?null:result;
 }
 // Flask's RFC/GMT serialization is explicitly zoned. Ambiguous legacy text stays raw.
 if(!/\b(?:GMT|UTC)\b|[+-]\d{4}$/.test(input))return null;
 const result=new Date(input);return Number.isNaN(result.getTime())?null:result;
}
export const getTimeZone=()=>{try{return Intl.DateTimeFormat().resolvedOptions().timeZone||'UTC';}catch{return 'UTC';}};
const usableFallback=(value,fallback)=>typeof value==='string'&&value.trim()?value:fallback;
export function formatDateTime(value,fallback='—',options={}) {
 const {locale,...settings}=options,day=typeof value==='string'?value.match(DAY):null;
 if(day){
  if(!calendarValid(+day[1],+day[2],+day[3]))return fallback;
  const calendar=new Date(0);calendar.setUTCFullYear(+day[1],+day[2]-1,+day[3]);calendar.setUTCHours(12,0,0,0);
  return new Intl.DateTimeFormat(locale,{year:'numeric',month:'short',day:'numeric',timeZone:'UTC'}).format(calendar);
 }
 const parsed=parseTimestamp(value);if(!parsed)return usableFallback(value,fallback);
 return new Intl.DateTimeFormat(locale,{year:'numeric',month:'short',day:'numeric',hour:'numeric',minute:'2-digit',second:'2-digit',timeZone:getTimeZone(),timeZoneName:'short',...settings}).format(parsed);
}
export function formatLocalTime(value=new Date(),fallback='—',options={}) {
 const parsed=parseTimestamp(value);if(!parsed)return fallback;
 const {locale,...settings}=options;
 return new Intl.DateTimeFormat(locale,{hour:'numeric',minute:'2-digit',second:'2-digit',timeZone:getTimeZone(),timeZoneName:'short',...settings}).format(parsed);
}
export function timeZoneLabel(value=new Date(),zone=getTimeZone()) {
 const parsed=parseTimestamp(value)||new Date();
 const parts=new Intl.DateTimeFormat(undefined,{timeZone:zone,timeZoneName:'short'}).formatToParts(parsed);
 return parts.find(p=>p.type==='timeZoneName')?.value||zone;
}
export function localCalendarDay(value=new Date(),zone=getTimeZone()) {
 if(typeof value==='string'&&DAY.test(value)){
  const [,y,m,d]=value.match(DAY);return calendarValid(+y,+m,+d)?value:'';
 }
 const parsed=parseTimestamp(value);if(!parsed)return '';
 const parts=new Intl.DateTimeFormat('en-US',{timeZone:zone,year:'numeric',month:'2-digit',day:'2-digit'}).formatToParts(parsed);
 const part=type=>parts.find(p=>p.type===type)?.value;
 return part('year')+'-'+part('month')+'-'+part('day');
}
export function nextScheduleRun(cadence,now=new Date()) {
 const date=parseTimestamp(now);if(!date)return null;
 date.setUTCHours(0,0,0,0);
 if(cadence==='weekly')date.setUTCDate(date.getUTCDate()+((8-date.getUTCDay())%7||7));
 else date.setUTCDate(date.getUTCDate()+1);
 return date;
}
export function scheduleLocalLabel(cadence,nextRun=null,options={}) {
 if(!['daily','weekly'].includes(cadence))return 'Schedule off';
 const parsed=parseTimestamp(nextRun)||nextScheduleRun(cadence);
 return (cadence==='daily'?'Daily':'Weekly')+' · '+formatLocalTime(parsed,'—',{second:undefined,...(cadence==='weekly'?{weekday:'long'}:{}),...options});
}

export function deadlinePassed(value,now=new Date()) {
 const parsed=parseTimestamp(value),clock=parseTimestamp(now);
 if(!clock)return false;
 if(parsed)return parsed.getTime()<=clock.getTime();
 // Legacy date-only deadlines have no clock time. Mark due after the whole local day.
 const day=typeof value==='string'&&DAY.test(value)?localCalendarDay(value):'';
 return Boolean(day&&day<localCalendarDay(clock));
}
